import { Box, Paper, Stack, Typography, alpha, useTheme } from '@mui/material'
import { format, parseISO } from 'date-fns'
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { marcasDeEixo } from '../../utils/formato'

export type PontoDiario = { data: string } & Record<string, number | string>

export type SerieDiaria = { chave: string; nome: string; cor: string }

type Props = {
  titulo: string
  pontos: PontoDiario[]
  /** Séries na ordem de empilhamento (de baixo para cima). Uma só = sem legenda. */
  series: SerieDiaria[]
  /** Valor completo, para o tooltip. */
  formatar: (v: number) => string
  /** Valor curto, para o eixo. */
  formatarEixo: (v: number) => string
}

const ALTURA = 240 // inclui a faixa do eixo X: nada de rolagem dentro do card

const valor = (p: PontoDiario, chave: string) => Number(p[chave] ?? 0)

/**
 * Colunas diárias, empilhadas quando há mais de uma série. Um eixo só por gráfico —
 * medidas de escalas diferentes vão em gráficos separados, lado a lado.
 */
export function ColunasDiarias({ titulo, pontos, series, formatar, formatarEixo }: Props) {
  const theme = useTheme()
  const superficie = theme.palette.background.paper
  const tinta = theme.palette.text.secondary
  const topo = series[series.length - 1]?.chave
  const maiorColuna = Math.max(0, ...pontos.map((pt) => series.reduce((s, x) => s + valor(pt, x.chave), 0)))
  const marcasY = marcasDeEixo(maiorColuna)

  return (
    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
      <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 1 }} useFlexGap flexWrap="wrap" spacing={1}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, fontFamily: '"Outfit", sans-serif' }}>
          {titulo}
        </Typography>
        {/* Legenda só com duas ou mais séries; com uma, o título já diz o que é. */}
        {series.length > 1 ? (
          <Stack direction="row" spacing={2} component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
            {series.map((s) => (
              <Stack key={s.chave} direction="row" spacing={0.75} alignItems="center" component="li">
                <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: s.cor }} aria-hidden />
                <Typography variant="caption" color="text.secondary">
                  {s.nome}
                </Typography>
              </Stack>
            ))}
          </Stack>
        ) : null}
      </Stack>

      <Box sx={{ height: ALTURA }}>
        <ResponsiveContainer>
          <BarChart data={pontos} margin={{ top: 8, right: 4, left: 0, bottom: 0 }} barCategoryGap="20%">
            <CartesianGrid vertical={false} stroke={theme.palette.divider} />
            <XAxis
              dataKey="data"
              tickFormatter={(d: string) => format(parseISO(d), 'dd/MM')}
              tick={{ fill: tinta, fontSize: 11 }}
              tickLine={false}
              axisLine={{ stroke: theme.palette.divider }}
              minTickGap={24}
              interval="preserveStartEnd"
            />
            <YAxis
              ticks={marcasY}
              domain={[0, marcasY[marcasY.length - 1]]}
              tickFormatter={formatarEixo}
              tick={{ fill: tinta, fontSize: 11, style: { fontVariantNumeric: 'tabular-nums' } }}
              tickLine={false}
              axisLine={false}
              width={64}
            />
            <Tooltip
              cursor={{ fill: alpha(theme.palette.text.primary, 0.06) }}
              content={({ active, payload, label }) => {
                if (!active || !payload?.length) return null
                const ponto = payload[0].payload as PontoDiario
                const total = series.reduce((s, x) => s + valor(ponto, x.chave), 0)
                return (
                  <Paper elevation={4} sx={{ px: 1.5, py: 1, minWidth: 180 }}>
                    <Typography variant="caption" color="text.secondary">
                      {format(parseISO(String(label)), 'dd/MM/yyyy')}
                    </Typography>
                    {[...series].reverse().map((s) => (
                      <Stack key={s.chave} direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                        <Box sx={{ width: 12, height: 2, bgcolor: s.cor, borderRadius: 1 }} aria-hidden />
                        <Typography variant="body2" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                          {formatar(valor(ponto, s.chave))}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {s.nome}
                        </Typography>
                      </Stack>
                    ))}
                    {series.length > 1 ? (
                      <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                        Total: <strong>{formatar(total)}</strong>
                      </Typography>
                    ) : null}
                  </Paper>
                )
              }}
            />
            {series.map((s) => (
              <Bar
                key={s.chave}
                dataKey={s.chave}
                stackId="dia"
                fill={s.cor}
                // 2px na cor do fundo separam os segmentos — não é uma borda, é espaço.
                stroke={superficie}
                strokeWidth={2}
                maxBarSize={24}
                // Arredonda só a ponta do dado, nunca a base.
                radius={s.chave === topo ? [4, 4, 0, 0] : 0}
                isAnimationActive={false}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Paper>
  )
}
